// Push to Institute Modal (UAS-2244)
// Two modes: create (first push) and edit (update existing push).
// Covers all 23 ACs from the spec.

import { useEffect, useRef, useState } from 'react';
import InternalAdminGrievanceService, {
    InternalGrievanceCard,
    SystemTag,
} from '../services/InternalAdminGrievanceService';
import type { GrievancePushRecord } from '../services/InternalAdminGrievanceService';
import { getPriorityLabel } from './helpers/statusBadge';

// ── Constants ────────────────────────────────────────────────────────────────

const MAX_TAGS = 3;
const MAX_CHARS = 1000;
const MSG_DISMISS_MS = 3000;

const PRIORITY_OPTIONS = [
    { value: 'critical', label: 'Critical' },
    { value: 'high',     label: 'High' },
    { value: 'medium',   label: 'Medium' },
    { value: 'low',      label: 'Low' },
];

// ── Types ────────────────────────────────────────────────────────────────────

export interface PushToInstituteModalProps {
    mode: 'create' | 'edit';
    grievance: InternalGrievanceCard;
    onClose: () => void;
    /** Called on successful create or edit with the updated push record. */
    onSuccess: (grievanceId: number, push: GrievancePushRecord) => void;
}

/** A chip in the combined tag selector (preset + system + custom). */
interface TagChipOption {
    tagCode: string;
    tagLabel: string;
    tagType: 'preset' | 'system' | 'custom';
    /** Bootstrap icon class — preset tags only */
    icon?: string;
    /** Bootstrap color suffix — preset tags only (e.g. 'success', 'warning') */
    colorClass?: string;
    /** System tag that was saved in edit mode but no longer qualifies — shown with warning */
    isStale?: boolean;
}

/** A tag currently selected (part of the submission payload). */
interface SelectedTag {
    tagCode: string;
    tagLabel: string;
    tagType: 'preset' | 'system' | 'custom';
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Slugify a custom tag label into a tag_code. */
const slugify = (label: string): string =>
    label.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

/** Bootstrap color map → inline style (for chips without a pre-seeded colorClass). */
const COLOR_STYLES: Record<string, { bg: string; text: string; border: string }> = {
    success:   { bg: '#f0fdf4', text: '#16a34a', border: '#86efac' },
    danger:    { bg: '#fef2f2', text: '#dc2626', border: '#fca5a5' },
    warning:   { bg: '#fffbeb', text: '#d97706', border: '#fde68a' },
    info:      { bg: '#ecfeff', text: '#0891b2', border: '#a5f3fc' },
    primary:   { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
    purple:    { bg: '#f5f3ff', text: '#7c3aed', border: '#ddd6fe' },
    pink:      { bg: '#fdf2f8', text: '#be185d', border: '#f9a8d4' },
    secondary: { bg: '#f8fafc', text: '#64748b', border: '#cbd5e1' },
    system:    { bg: '#f5f3ff', text: '#6d28d9', border: '#ddd6fe' },
    custom:    { bg: '#f0fdfa', text: '#0f766e', border: '#99f6e4' },
};

const getChipStyle = (chip: TagChipOption, selected: boolean): React.CSSProperties => {
    const colorKey = chip.tagType === 'custom'
        ? 'custom'
        : (chip.colorClass ?? 'secondary');
    const s = COLOR_STYLES[colorKey] ?? COLOR_STYLES['secondary'];
    return selected
        ? { backgroundColor: s.bg, color: s.text, borderColor: s.border, borderWidth: 2 }
        : { backgroundColor: '#fff', color: '#475569', borderColor: '#e2e8f0' };
};

// ── Component ─────────────────────────────────────────────────────────────────

const PushToInstituteModal = ({ mode, grievance, onClose, onSuccess }: PushToInstituteModalProps) => {
    // ── Data loading ─────────────────────────────────────────────────────────
    const [loading, setLoading]     = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    // Chips in the selector: System (from API, conditionally shown) + Custom (admin-entered)
    const [systemChips, setSystemChips]   = useState<TagChipOption[]>([]);
    // Custom chips created in this session (or pre-filled from edit mode)
    const [customChips, setCustomChips]   = useState<TagChipOption[]>([]);

    // Set of selected tagCodes
    const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());

    // ── Form fields ──────────────────────────────────────────────────────────
    const [teamNotes, setTeamNotes]                 = useState('');
    const [suggestedResolution, setSuggestedResolution] = useState('');
    const [institutePriority, setInstitutePriority] = useState(''); // '' = same as internal

    // ── Inline messages ──────────────────────────────────────────────────────
    const [tagLimitMsg, setTagLimitMsg]     = useState<string | null>(null);
    const [duplicateMsg, setDuplicateMsg]   = useState<string | null>(null);
    const [fieldErrors, setFieldErrors]     = useState<{ teamNotes?: string; suggestedResolution?: string }>({});
    const [saveError, setSaveError]         = useState<string | null>(null);

    // ── Toast (non-blocking recheck — AC20) ─────────────────────────────────
    const [showRecheckToast, setShowRecheckToast] = useState(false);

    // ── Discard confirmation (AC21) ──────────────────────────────────────────
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

    // ── Submit loading state ─────────────────────────────────────────────────
    const [saving, setSaving] = useState(false);

    // ── Custom tag input ─────────────────────────────────────────────────────
    const [customInput, setCustomInput] = useState('');

    // ── Initial values snapshot for dirty check (AC21) ──────────────────────
    const initialRef = useRef<{
        selectedCodes: Set<string>;
        teamNotes: string;
        suggestedResolution: string;
        institutePriority: string;
    } | null>(null);

    // ── Load system tags on mount ─────────────────────────────────────────────
    // GRIEVANCE_PRESET_TAG in domain_data is metadata-only (icon/color).
    // The system-tags API returns only tags the alumni qualifies for, enriched with that metadata.
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setLoadError(null);

        InternalAdminGrievanceService.getSystemTags(grievance.id)
            .then((systems) => {
                if (cancelled) return;

                // Build chips from system tags (icon/color come from backend enrichment)
                const systemOptions: TagChipOption[] = (systems as SystemTag[]).map((s) => ({
                    tagCode:    s.tagCode,
                    tagLabel:   s.tagLabel,
                    tagType:    'system',
                    icon:       s.icon ?? undefined,
                    colorClass: s.colorClass ?? undefined,
                }));

                setSystemChips(systemOptions);

                // ── Edit mode: pre-fill from saved push (AC3) ────────────────
                if (mode === 'edit' && grievance.push) {
                    const saved = grievance.push;
                    const initNotes      = saved.teamNotes ?? '';
                    const initResolution = saved.suggestedResolution ?? '';
                    const initPriority   = saved.institutePriority ?? '';

                    setTeamNotes(initNotes);
                    setSuggestedResolution(initResolution);
                    setInstitutePriority(initPriority);

                    const qualifyingSystemCodes = new Set(systems.map((s: SystemTag) => s.tagCode));
                    const initSelectedCodes = new Set<string>();
                    const savedCustomChips: TagChipOption[] = [];

                    saved.tags.forEach((t) => {
                        initSelectedCodes.add(t.tagCode);
                        if (t.tagType === 'custom') {
                            savedCustomChips.push({ tagCode: t.tagCode, tagLabel: t.tagLabel, tagType: 'custom' });
                        }
                        // AC20: detect stale system tags (no longer qualifying for this alumni)
                        if ((t.tagType === 'system' || t.tagType === 'preset') && !qualifyingSystemCodes.has(t.tagCode)) {
                            const staleIdx = systemOptions.findIndex((c) => c.tagCode === t.tagCode);
                            if (staleIdx === -1) {
                                systemOptions.push({ tagCode: t.tagCode, tagLabel: t.tagLabel, tagType: 'system', isStale: true });
                            } else {
                                systemOptions[staleIdx] = { ...systemOptions[staleIdx], isStale: true };
                            }
                        }
                    });

                    setSystemChips([...systemOptions]);
                    setCustomChips(savedCustomChips);
                    setSelectedCodes(initSelectedCodes);

                    initialRef.current = {
                        selectedCodes:       new Set(initSelectedCodes),
                        teamNotes:           initNotes,
                        suggestedResolution: initResolution,
                        institutePriority:   initPriority,
                    };
                } else {
                    // ── Create mode: auto-select all qualifying system tags up to cap (AC17) ──
                    const autoSelect = new Set<string>();
                    for (const chip of systemOptions) {
                        if (autoSelect.size < MAX_TAGS) autoSelect.add(chip.tagCode);
                    }
                    setSelectedCodes(autoSelect);

                    initialRef.current = {
                        selectedCodes:       new Set(autoSelect),
                        teamNotes:           '',
                        suggestedResolution: '',
                        institutePriority:   '',
                    };
                }
            })
            .catch(() => setLoadError('Failed to load tag options. Please try again.'))
            .finally(() => { if (!cancelled) setLoading(false); });

        return () => { cancelled = true; };
    }, [grievance.id, mode]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Auto-dismiss inline tag messages (AC7, AC18) ─────────────────────────
    useEffect(() => {
        if (!tagLimitMsg) return;
        const t = setTimeout(() => setTagLimitMsg(null), MSG_DISMISS_MS);
        return () => clearTimeout(t);
    }, [tagLimitMsg]);

    useEffect(() => {
        if (!duplicateMsg) return;
        const t = setTimeout(() => setDuplicateMsg(null), MSG_DISMISS_MS);
        return () => clearTimeout(t);
    }, [duplicateMsg]);

    // ── Dirty check ──────────────────────────────────────────────────────────
    const isDirty = (): boolean => {
        const snap = initialRef.current;
        if (!snap) return false;
        if (teamNotes !== snap.teamNotes) return true;
        if (suggestedResolution !== snap.suggestedResolution) return true;
        if (institutePriority !== snap.institutePriority) return true;
        if (selectedCodes.size !== snap.selectedCodes.size) return true;
        for (const code of selectedCodes) {
            if (!snap.selectedCodes.has(code)) return true;
        }
        return false;
    };

    // ── Close guard (AC21) ───────────────────────────────────────────────────
    const handleCloseAttempt = () => {
        if (isDirty()) {
            setShowDiscardConfirm(true);
        } else {
            onClose();
        }
    };

    // ── Tag toggle (AC5) ─────────────────────────────────────────────────────
    const handleChipToggle = (chip: TagChipOption) => {
        const isSelected = selectedCodes.has(chip.tagCode);
        if (isSelected) {
            const next = new Set(selectedCodes);
            next.delete(chip.tagCode);
            setSelectedCodes(next);
        } else {
            if (selectedCodes.size >= MAX_TAGS) {
                // AC7: 4th selection blocked
                setTagLimitMsg('Maximum 3 tags allowed');
                return;
            }
            setSelectedCodes(new Set([...selectedCodes, chip.tagCode]));
        }
    };

    // ── Custom tag add (AC6) ─────────────────────────────────────────────────
    const handleAddCustomTag = () => {
        const label = customInput.trim();
        if (!label) return;

        // AC18: duplicate check against all existing labels
        const allChips = [...systemChips, ...customChips];
        const normalised = label.toLowerCase();
        if (allChips.some((c) => c.tagLabel.toLowerCase() === normalised)) {
            setDuplicateMsg('Tag already exists');
            return;
        }

        // AC7: cap check
        if (selectedCodes.size >= MAX_TAGS) {
            setTagLimitMsg('Maximum 3 tags allowed');
            return;
        }

        const tagCode = slugify(label) || `custom_${Date.now()}`;
        const newChip: TagChipOption = { tagCode, tagLabel: label, tagType: 'custom' };
        setCustomChips((prev) => [...prev, newChip]);
        setSelectedCodes(new Set([...selectedCodes, tagCode]));
        setCustomInput('');
    };

    const handleCustomInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') { e.preventDefault(); handleAddCustomTag(); }
    };

    // ── Build current selected tags list (for submission) ────────────────────
    const buildSelectedTagList = (): SelectedTag[] => {
        const allChips = [...systemChips, ...customChips];
        const result: SelectedTag[] = [];
        let order = 1;
        for (const chip of allChips) {
            if (selectedCodes.has(chip.tagCode)) {
                result.push({ tagCode: chip.tagCode, tagLabel: chip.tagLabel, tagType: chip.tagType });
                if (++order > MAX_TAGS) break;
            }
        }
        return result;
    };

    // ── Recheck toast detection (AC20) ──────────────────────────────────────
    const hasStaleSystemTag = (): boolean => {
        const allChips = [...systemChips, ...customChips];
        return allChips.some((c) => c.isStale && selectedCodes.has(c.tagCode));
    };

    // ── Submit (AC9, AC10, AC12, AC13, AC20) ─────────────────────────────────
    const handleSubmit = async () => {
        // AC9: validate required fields
        const errors: typeof fieldErrors = {};
        if (!teamNotes.trim()) errors.teamNotes = 'Univariety Team Notes is required.';
        if (!suggestedResolution.trim()) errors.suggestedResolution = 'Suggested Resolution is required.';
        if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }
        setFieldErrors({});

        // AC20: recheck toast for stale system tags (non-blocking — show then allow)
        if (hasStaleSystemTag()) {
            setShowRecheckToast(true);
            // Allow a beat for the toast to render before continuing
            await new Promise((r) => setTimeout(r, 0));
        }

        // Include any custom tag still pending in the input (user didn't click "+ Add")
        let effectiveCustomChips = customChips;
        let effectiveSelectedCodes = selectedCodes;
        const pendingLabel = customInput.trim();
        if (pendingLabel) {
            const allExisting = [...systemChips, ...customChips];
            const isDuplicate = allExisting.some((c) => c.tagLabel.toLowerCase() === pendingLabel.toLowerCase());
            if (!isDuplicate && effectiveSelectedCodes.size < MAX_TAGS) {
                const tagCode = slugify(pendingLabel) || `custom_${Date.now()}`;
                effectiveCustomChips = [...effectiveCustomChips, { tagCode, tagLabel: pendingLabel, tagType: 'custom' }];
                effectiveSelectedCodes = new Set([...effectiveSelectedCodes, tagCode]);
            }
        }

        // Build tag list in render order: system → custom
        const allChipsForPayload = [...systemChips, ...effectiveCustomChips];
        const builtTags: SelectedTag[] = [];
        let order = 1;
        for (const chip of allChipsForPayload) {
            if (effectiveSelectedCodes.has(chip.tagCode)) {
                builtTags.push({ tagCode: chip.tagCode, tagLabel: chip.tagLabel, tagType: chip.tagType });
                if (++order > MAX_TAGS) break;
            }
        }

        const payload = {
            teamNotes:          teamNotes.trim(),
            suggestedResolution: suggestedResolution.trim(),
            institutePriority:  institutePriority || null,
            tags:               builtTags,
        };

        setSaving(true);
        setSaveError(null);
        try {
            let pushRecord: GrievancePushRecord;
            if (mode === 'create') {
                const res = await InternalAdminGrievanceService.createPush(grievance.id, payload);
                // Build optimistic push record for immediate row update (AC12)
                pushRecord = {
                    id:                  res.pushId,
                    institutePriority:   payload.institutePriority,
                    teamNotes:           payload.teamNotes,
                    suggestedResolution: payload.suggestedResolution,
                    tags:                payload.tags,
                };
            } else {
                await InternalAdminGrievanceService.updatePush(grievance.id, payload);
                // Build updated push record for row refresh (AC13)
                pushRecord = {
                    id:                  grievance.push!.id,
                    institutePriority:   payload.institutePriority,
                    teamNotes:           payload.teamNotes,
                    suggestedResolution: payload.suggestedResolution,
                    tags:                payload.tags,
                };
            }
            onSuccess(grievance.id, pushRecord);
            onClose();
        } catch {
            setSaveError('Failed to save. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // ── Derived values ────────────────────────────────────────────────────────
    const selectedCount = selectedCodes.size;
    const allChips      = [...systemChips, ...customChips];

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <>
            {/* ── Main Modal ────────────────────────────────────────────── */}
            <div
                className="modal fade show d-block"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                onClick={(e) => { if (e.target === e.currentTarget) handleCloseAttempt(); }}
            >
                <div
                    className="modal-dialog modal-dialog-centered modal-dialog-scrollable mx-auto"
                    style={{ maxWidth: 700 }}
                >
                    <div className="modal-content radius-15 d-flex flex-column" style={{ maxHeight: '90vh' }}>

                        {/* ── Header (AC1, AC2, AC16) ────────────────────── */}
                        <div className="modal-header flex-column justify-content-start align-items-start flex-shrink-0">
                            {/* AC1 / AC2: mode-dependent title */}
                            <h5 className="modal-title fw-medium">
                                {mode === 'create' ? 'Push to Institute' : 'Edit Push Details'}
                            </h5>
                            {/* AC16: subtitle format <code> · <alumni name> */}
                            <p className="text-secondary mb-0 font-13">
                                {grievance.code} · {grievance.alumni.name}
                            </p>
                            <a
                                href="#"
                                className="position-absolute top-20 right-20 m-0 text-secondary font-30"
                                onClick={(e) => { e.preventDefault(); handleCloseAttempt(); }}
                                aria-label="Close"
                            >
                                <i className="bi bi-x-circle-fill"></i>
                            </a>
                        </div>

                        {/* ── Scrollable body ─────────────────────────────── */}
                        <div className="modal-body p-4 overflow-auto flex-grow-1">
                            {loading && (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading…</span>
                                    </div>
                                </div>
                            )}

                            {loadError && !loading && (
                                <div className="alert alert-danger">{loadError}</div>
                            )}

                            {!loading && !loadError && (
                                <>
                                    {/* ── Alumni Importance Tags (AC4–AC8, AC17–AC19, AC23) ── */}
                                    <div className="mb-4">
                                        <div className="font-14 fw-semibold text-uppercase border-bottom pb-1 mb-2 text-secondary">
                                            Alumni Importance Tags{' '}
                                            <span className="text-lowercase font-12" style={{ color: '#93a4bf' }}>
                                                (max {MAX_TAGS})
                                            </span>
                                        </div>
                                        <p className="font-14 mb-3 text-secondary">
                                            Select from preset tags or add a custom one. These help the institute understand the alumni's significance.
                                        </p>

                                        {/* AC4, AC5, AC15, AC17, AC23: chip grid */}
                                        {allChips.length > 0 ? (
                                            <div className="d-flex flex-wrap gap-2 mb-2">
                                                {allChips.map((chip) => {
                                                    const selected = selectedCodes.has(chip.tagCode);
                                                    return (
                                                        <button
                                                            key={chip.tagCode}
                                                            type="button"
                                                            className="btn btn-sm rounded-pill px-3 py-2 font-13 fw-500 d-inline-flex align-items-center gap-1"
                                                            style={{
                                                                ...getChipStyle(chip, selected),
                                                                border: '1.5px solid',
                                                                transition: 'all 0.15s',
                                                            }}
                                                            onClick={() => handleChipToggle(chip)}
                                                            title={chip.isStale ? 'This tag no longer qualifies for this alumni' : undefined}
                                                        >
                                                            {/* System chip: icon from domain_data metadata if available */}
                                                            {chip.tagType === 'system' && chip.icon && (
                                                                <i className={`bi ${chip.icon} font-12${chip.isStale ? ' text-warning' : ''}`}></i>
                                                            )}
                                                            {/* Custom: tag icon */}
                                                            {chip.tagType === 'custom' && (
                                                                <i className="bi bi-tag font-12"></i>
                                                            )}
                                                            <span>{chip.tagLabel}</span>
                                                            {/* AC: stale warning icon */}
                                                            {chip.isStale && (
                                                                <i className="bi bi-exclamation-triangle-fill text-warning font-11 ms-1"
                                                                   title="No longer qualifying"></i>
                                                            )}
                                                            {/* AC5: selected checkmark */}
                                                            {selected && (
                                                                <i className="bi bi-check-lg font-13 ms-1"></i>
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="text-secondary small fst-italic mb-2">
                                                No qualifying tags for this alumni. Use the custom input below.
                                            </p>
                                        )}

                                        {/* AC6: Custom tag input */}
                                        <div className="row g-2 align-items-center mb-1">
                                            <div className="col-sm">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Add custom tag…"
                                                    value={customInput}
                                                    onChange={(e) => setCustomInput(e.target.value)}
                                                    onKeyDown={handleCustomInputKeyDown}
                                                    maxLength={255}
                                                />
                                            </div>
                                            <div className="col-sm-auto">
                                                <button
                                                    type="button"
                                                    className="btn btn-light border text-secondary fw-500 px-3 w-100 height-45"
                                                    onClick={handleAddCustomTag}
                                                >
                                                    + Add
                                                </button>
                                            </div>
                                        </div>

                                        {/* AC7: 4-tag block message (auto-dismisses) */}
                                        {tagLimitMsg && (
                                            <div className="text-danger font-13 mt-1">
                                                <i className="bi bi-exclamation-circle me-1"></i>
                                                {tagLimitMsg}
                                            </div>
                                        )}

                                        {/* AC18: duplicate message (auto-dismisses) */}
                                        {duplicateMsg && (
                                            <div className="text-warning font-13 mt-1">
                                                <i className="bi bi-exclamation-triangle me-1"></i>
                                                {duplicateMsg}
                                            </div>
                                        )}

                                        {/* AC8: live counter */}
                                        <div className="font-14 mt-2" style={{ color: '#a7b3c5' }}>
                                            {selectedCount} / {MAX_TAGS} tags selected
                                        </div>
                                    </div>

                                    {/* ── Univariety Team Notes (AC9, AC10, AC22) ── */}
                                    <div className="mb-4">
                                        <label className="form-label fw-semibold">
                                            Univariety Team Notes{' '}
                                            <span className="text-danger">*</span>
                                        </label>
                                        <textarea
                                            className={`form-control h-auto${fieldErrors.teamNotes ? ' is-invalid' : ''}`}
                                            rows={4}
                                            placeholder="Add context about this alumni and why this grievance needs attention…"
                                            value={teamNotes}
                                            maxLength={MAX_CHARS}
                                            onChange={(e) => {
                                                setTeamNotes(e.target.value);
                                                if (fieldErrors.teamNotes) setFieldErrors((f) => ({ ...f, teamNotes: undefined }));
                                            }}
                                        />
                                        <div className="d-flex justify-content-between mt-1">
                                            {fieldErrors.teamNotes
                                                ? <span className="text-danger font-12">{fieldErrors.teamNotes}</span>
                                                : <span />
                                            }
                                            {/* AC22: N remaining format */}
                                            <span
                                                className="font-12 ms-auto"
                                                style={{ color: teamNotes.length >= MAX_CHARS ? '#dc2626' : '#94a3b8' }}
                                            >
                                                {MAX_CHARS - teamNotes.length} remaining
                                            </span>
                                        </div>
                                    </div>

                                    {/* ── Suggested Resolution (AC9, AC10, AC22) ── */}
                                    <div className="mb-4">
                                        <label className="form-label fw-semibold">
                                            Suggested Resolution{' '}
                                            <span className="text-danger">*</span>
                                        </label>
                                        <textarea
                                            className={`form-control h-auto${fieldErrors.suggestedResolution ? ' is-invalid' : ''}`}
                                            rows={4}
                                            placeholder="Provide a recommended course of action for the institute admin…"
                                            value={suggestedResolution}
                                            maxLength={MAX_CHARS}
                                            onChange={(e) => {
                                                setSuggestedResolution(e.target.value);
                                                if (fieldErrors.suggestedResolution) setFieldErrors((f) => ({ ...f, suggestedResolution: undefined }));
                                            }}
                                        />
                                        <div className="d-flex justify-content-between mt-1">
                                            {fieldErrors.suggestedResolution
                                                ? <span className="text-danger font-12">{fieldErrors.suggestedResolution}</span>
                                                : <span />
                                            }
                                            <span
                                                className="font-12 ms-auto"
                                                style={{ color: suggestedResolution.length >= MAX_CHARS ? '#dc2626' : '#94a3b8' }}
                                            >
                                                {MAX_CHARS - suggestedResolution.length} remaining
                                            </span>
                                        </div>
                                    </div>

                                    {/* ── Priority for Institute (AC11) ────────── */}
                                    <div className="mb-2">
                                        <label className="form-label fw-semibold">Priority for Institute</label>
                                        <select
                                            className="form-select"
                                            value={institutePriority}
                                            onChange={(e) => setInstitutePriority(e.target.value)}
                                        >
                                            <option value="">
                                                -- Same as internal priority ({getPriorityLabel(grievance.priority)}) --
                                            </option>
                                            {PRIORITY_OPTIONS.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Save error */}
                                    {saveError && (
                                        <div className="alert alert-danger mt-3 mb-0">{saveError}</div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* ── Sticky footer (AC14) ────────────────────────── */}
                        <div className="modal-footer flex-shrink-0">
                            <button
                                type="button"
                                className="btn btn-border-gray px-3"
                                onClick={handleCloseAttempt}
                                disabled={saving}
                            >
                                <span>Cancel</span>
                            </button>
                            {/* AC1/AC2: mode-dependent primary button label */}
                            <button
                                type="button"
                                className="btn btn-dark-blue px-3 height-35 font-400 text-white"
                                onClick={handleSubmit}
                                disabled={saving || loading}
                            >
                                <span className="font-400">
                                    {saving
                                        ? 'Saving…'
                                        : mode === 'create'
                                            ? 'Push to Institute'
                                            : 'Save Changes'}
                                </span>
                            </button>
                        </div>

                    </div>
                </div>
            </div>

            {/* ── AC20: Recheck toast (non-blocking) ──────────────────── */}
            {showRecheckToast && (
                <div
                    className="position-fixed bottom-0 end-0 p-3"
                    style={{ zIndex: 1060 }}
                >
                    <div className="toast show align-items-center text-white bg-warning border-0" role="alert">
                        <div className="d-flex">
                            <div className="toast-body fw-500">
                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                Please recheck the selected tags before pushing.
                            </div>
                            <button
                                type="button"
                                className="btn-close btn-close-white me-2 m-auto"
                                onClick={() => setShowRecheckToast(false)}
                                aria-label="Close"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* ── AC21: Discard changes confirmation ──────────────────── */}
            {showDiscardConfirm && (
                <div
                    className="modal fade show d-block"
                    style={{ backgroundColor: 'rgba(0,0,0,0.3)', zIndex: 1060 }}
                    tabIndex={-1}
                    role="dialog"
                >
                    <div className="modal-dialog modal-dialog-centered modal-sm">
                        <div className="modal-content">
                            <div className="modal-header border-0 pb-0">
                                <h6 className="modal-title fw-semibold">Discard changes?</h6>
                            </div>
                            <div className="modal-body pt-2">
                                <p className="text-secondary font-14 mb-0">
                                    You have unsaved changes. Are you sure you want to close?
                                </p>
                            </div>
                            <div className="modal-footer border-0 pt-0">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setShowDiscardConfirm(false)}
                                >
                                    Keep editing
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    onClick={() => { setShowDiscardConfirm(false); onClose(); }}
                                >
                                    Discard
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PushToInstituteModal;
